import React, { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { fetchCurrentUser } from '../services/authService';
import { useIdleTimer } from '../hooks/useIdleTimer';
import api from '../config/api';
import type { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | null>(null);

const SESSION_WARNING_MINUTES = 2;

// Dynamically load antd to avoid pulling chunks into the main bundle on cold load
async function showWarningModal(opts: { title: string; content: string; okText: string; cancelText: string; onOk: () => void; onCancel: () => void }) {
  const { Modal } = await import('antd');
  Modal.warning({ ...opts, okCancel: true });
}

async function showToast(text: string, type: 'warning' | 'success' = 'warning') {
  const { message } = await import('antd');
  message[type](text);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [expiresIn, setExpiresIn] = useState<number>(0);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState<number>(5);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expiryWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExpiryTimers = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (expiryWarningTimerRef.current) {
      clearTimeout(expiryWarningTimerRef.current);
      expiryWarningTimerRef.current = null;
    }
  }, []);

  const doLogout = useCallback(async (reason: string) => {
    try {
      await api.post('/api/auth/logout', { logout_reason: reason });
    } catch {
      // Best-effort
    }
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('AML.SYSTEM_username');
    setUser(null);
    setPermissions([]);
    setRoles([]);
    setExpiresIn(0);
    clearExpiryTimers();
  }, [clearExpiryTimers]);

  // Idle timer — auto-logout after idle timeout
  const { resetTimer: resetIdleTimer, broadcastLogout } = useIdleTimer({
    idleTimeoutMinutes,
    enabled: !!user && idleTimeoutMinutes > 0,
    onIdle: () => {
      showToast('You have been automatically logged out due to inactivity.', 'warning');
      doLogout('idle').then(() => {
        broadcastLogout();
        window.location.href = '/login?reason=idle';
      });
    },
    onWarning: () => {
      showWarningModal({
        title: 'Session Expiring Due to Inactivity',
        content: 'Your session is about to expire due to inactivity. Click "Stay Logged In" to continue.',
        okText: 'Stay Logged In',
        cancelText: 'Logout Now',
        onOk: () => {
          resetIdleTimer();
        },
        onCancel: () => {
          doLogout('idle').then(() => {
            window.location.href = '/login?reason=idle';
          });
        },
      });
    },
  });

  const startExpiryCountdown = useCallback((seconds: number) => {
    clearExpiryTimers();

    if (seconds <= 0) return;

    const warningSeconds = SESSION_WARNING_MINUTES * 60;

    if (seconds > warningSeconds) {
      expiryWarningTimerRef.current = setTimeout(() => {
        showWarningModal({
          title: 'Session Expiring',
          content: `Your session will expire in ${SESSION_WARNING_MINUTES} minutes. Would you like to extend your session?`,
          okText: 'Extend Session',
          cancelText: 'Logout',
          onOk: async () => {
            try {
              await api.post('/api/auth/refresh');
              showToast('Session extended', 'success');
            } catch {
              doLogout('expired').then(() => {
                window.location.href = '/login?reason=expired';
              });
            }
          },
          onCancel: () => {
            doLogout('expired').then(() => {
              window.location.href = '/login?reason=expired';
            });
          },
        });
      }, (seconds - warningSeconds) * 1000);
    }

    expiryTimerRef.current = setTimeout(() => {
      doLogout('expired').then(() => {
        window.location.href = '/login?reason=expired';
      });
    }, seconds * 1000);
  }, [clearExpiryTimers, doLogout]);

  const fetchMe = useCallback(async () => {
    try {
      const u = await fetchCurrentUser();
      setUser(u);
      setPermissions(u.permissions || []);
      setRoles(u.roles || []);
      if (u.username) localStorage.setItem('AML.SYSTEM_username', u.username);
      else if (u.full_name) localStorage.setItem('AML.SYSTEM_username', u.full_name);
      if (u.is_superuser) localStorage.setItem('is_superuser', 'true');
    } catch {
      setUser(null);
      setPermissions([]);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const isSuperuser = user?.is_superuser === true;
  const isAdmin = isSuperuser || roles.includes('Administrator');

  const hasPermission = useCallback((action: string): boolean => {
    if (isAdmin) return true;
    return permissions.includes(action);
  }, [isAdmin, permissions]);

  const hasAnyPermission = useCallback((actions: string[]): boolean => {
    if (isAdmin) return true;
    return actions.some(a => permissions.includes(a));
  }, [isAdmin, permissions]);

  const hasRole = useCallback((roleName: string): boolean => {
    return roles.includes(roleName);
  }, [roles]);

  // Login: called after successful login API call
  const login = useCallback(async (accessToken?: string, requiresPwdChange?: boolean) => {
    if (requiresPwdChange) {
      setRequiresPasswordChange(true);
    }
    await fetchMe();
  }, [fetchMe]);

  const clearRequiresPasswordChange = useCallback(() => {
    setRequiresPasswordChange(false);
  }, []);

  // Set session timers (called from LoginPage after successful login)
  const setupSessionTimers = useCallback((tokenExpiresIn: number, idleMin: number) => {
    setExpiresIn(tokenExpiresIn);
    setIdleTimeoutMinutes(idleMin);
    startExpiryCountdown(tokenExpiresIn);
  }, [startExpiryCountdown]);

  // Expose setupSessionTimers via a ref for the LoginPage to use
  const setupTimersRef = useRef(setupSessionTimers);
  // Keep ref in sync
  setupTimersRef.current = setupSessionTimers;

  // Logout
  const logout = useCallback(async () => {
    await doLogout('manual');
  }, [doLogout]);

  const value: AuthContextType = {
    user,
    permissions,
    roles,
    loading,
    isSuperuser,
    isAdmin,
    hasPermission,
    hasAnyPermission,
    hasRole,
    login,
    logout,
    expiresIn,
    idleTimeoutMinutes,
    requiresPasswordChange,
    clearRequiresPasswordChange,
    setupTimersRef,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    return {
      user: null,
      permissions: [],
      roles: [],
      loading: false,
      isSuperuser: false,
      isAdmin: false,
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasRole: () => false,
      login: async () => { },
      logout: () => { },
      expiresIn: 0,
      idleTimeoutMinutes: 5,
      requiresPasswordChange: false,
      clearRequiresPasswordChange: () => { },
      setupTimersRef: { current: null },
    };
  }
  return context;
}

export default AuthContext;
